// eslint-disable-next-line eag/no-external-date-time-format-functions -- Custom format
import {Card, CardVariantType, Form, FormField} from 'platform/components';
import {HStack, Show, Spinner, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useRef} from 'react';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useDebouncedValue} from 'shared/web';

export interface MetadaNoteCardProps extends TestIdProps {
  cardVariant?: CardVariantType;
  note?: string | null;
  isLoading?: boolean;
  isDisabled?: boolean;
  title?: string;
  onChange: (values?: MetadaNoteCardFormSchema) => void;
}

const ONCHANGE_TIMEOUT = 500;
const NOTE_DEBOUNCE_TIMEOUT = 2000;

export function MetadaNoteCard(props: MetadaNoteCardProps) {
  const changeDebounce = useRef<ReturnType<typeof setTimeout>>(null);
  const debouncedNote = useDebouncedValue(props.note ?? null, NOTE_DEBOUNCE_TIMEOUT);

  const onChange = (values: MetadaNoteCardFormSchema) => {
    if (changeDebounce.current) {
      clearTimeout(changeDebounce.current);
    }

    changeDebounce.current = setTimeout(() => {
      props.onChange(values);
    }, ONCHANGE_TIMEOUT);
  };

  const defaultValues: MetadaNoteCardFormSchema = {note: debouncedNote};

  return (
    <Card
      title={props.title ?? i18n.t('general.labels.note')}
      variant={props.cardVariant}
      data-testid={suffixTestId('note', props)}
    >
      <Form<MetadaNoteCardFormSchema>
        experimentalZodSchema={schema}
        defaultValues={defaultValues}
        onChange={onChange}
        mode="onBlur"
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              name="note"
              type="textarea"
              control={control}
              minRows={4}
              data-testid={suffixTestId('note', props)}
              isDisabled={props.isDisabled}
            />
            <HStack spacing={1} justify="flex-end" align="center">
              <Show when={props.isLoading}>
                <Spinner size="small" data-testid={suffixTestId('dataStatus-spinner', props)} />
              </Show>
            </HStack>
          </VStack>
        )}
      </Form>
    </Card>
  );
}

const schema = z.object({
  note: z.string().nullable(),
});

type MetadaNoteCardFormSchema = z.infer<typeof schema>;
